import React, { useState, useEffect } from 'react';
import { api } from '../api';

const STATUS_CONFIG = {
  draft:     { label: 'Brouillon',  color: '#c084fc', bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.3)' },
  pending:   { label: 'Envoyée',    color: '#fb923c', bg: 'rgba(251,146,60,0.15)', border: 'rgba(251,146,60,0.3)' },
  paid:      { label: 'Payée',      color: '#4ade80', bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.3)' },
  cancelled: { label: 'Annulée',   color: '#f87171', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.3)' },
};

const STATUS_TRANSITIONS = {
  draft:     ['pending', 'cancelled'],
  pending:   ['paid', 'cancelled'],
  paid:      [],
  cancelled: ['draft'],
};

const STATUS_LABELS = {
  pending:   'Marquer envoyée',
  paid:      'Marquer payée',
  cancelled: 'Annuler',
  draft:     'Remettre en brouillon',
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtAmount = (n) => parseFloat(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid #1e293b' }}>
    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{label}</span>
    <span style={{ color: '#e2e8f0', fontWeight: '500', fontSize: '0.9rem' }}>{value || '—'}</span>
  </div>
);

const InvoiceDetail = ({ invoiceId, artisanProfile, onBack, toast }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [facturxLoading, setFacturxLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.getInvoice(invoiceId)
      .then(d => setInvoice(d.invoice))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const handleStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      const d = await api.updateInvoiceStatus(invoiceId, newStatus);
      setInvoice(d.invoice);
      toast(`Statut mis à jour : ${STATUS_CONFIG[newStatus].label}`, 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await api.downloadInvoicePdf(invoiceId, invoice.number);
      toast(`PDF ${invoice.number} téléchargé`, 'success');
    } catch (e) {
      toast(e.message || 'Erreur PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFacturx = async () => {
    setFacturxLoading(true);
    try {
      await api.downloadFacturX(invoiceId, invoice.number);
      toast('Factur-X XML téléchargé', 'success');
    } catch (e) {
      toast(e.message || 'Erreur Factur-X', 'error');
    } finally {
      setFacturxLoading(false);
    }
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const d = await api.shareInvoice(invoiceId);
      setShareUrl(d.url);
      await navigator.clipboard.writeText(d.url);
      toast('Lien copié dans le presse-papier !', 'success');
    } catch (e) {
      toast(e.message || 'Erreur génération lien', 'error');
    } finally {
      setShareLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #334155', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!invoice) return (
    <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
      <p>Facture introuvable</p>
      <button onClick={onBack} style={{ marginTop: '1rem', background: 'none', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.5rem', padding: '0.5rem 1.2rem', cursor: 'pointer' }}>Retour</button>
    </div>
  );

  const sc = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const transitions = STATUS_TRANSITIONS[invoice.status] || [];
  const ttc     = parseFloat(invoice.amountTtc ?? invoice.amount ?? 0);
  const tvaRate = parseFloat(invoice.tvaRate ?? 20);
  const ht      = invoice.amountHt  != null ? parseFloat(invoice.amountHt)  : ttc / (1 + tvaRate / 100);
  const tva     = invoice.tvaAmount != null ? parseFloat(invoice.tvaAmount) : ttc - ht;
  const tvaLabel = tvaRate === 0 ? 'TVA (franchise — art. 293B)' : `TVA (${tvaRate}%)`;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onBack}
            style={{ background: 'rgba(51,65,85,0.6)', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            ← Retour
          </button>
          <div>
            <h2 style={{ color: 'white', margin: 0, fontSize: '1.4rem' }}>📄 {invoice.number}</h2>
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Créée le {fmt(invoice.created_at)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', position: 'relative' }}>
          {/* Statut badge */}
          <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: '999px', padding: '0.35rem 1rem', fontSize: '0.85rem', fontWeight: '700' }}>
            {sc.label}
          </span>
          {/* Bouton PDF */}
          <button
            onClick={handlePdf}
            disabled={pdfLoading}
            style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '0.5rem', padding: '0.5rem 1.1rem', cursor: pdfLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: pdfLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {pdfLoading ? '⏳' : '⬇'} PDF
          </button>
          {/* Bouton Factur-X */}
          <button
            onClick={handleFacturx}
            disabled={facturxLoading}
            title="Télécharger le XML Factur-X (conforme EN 16931)"
            style={{ background: 'rgba(234,179,8,0.12)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.3)', borderRadius: '0.5rem', padding: '0.5rem 1.1rem', cursor: facturxLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: facturxLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {facturxLoading ? '⏳' : '📋'} Factur-X
          </button>
          {/* Bouton Partager */}
          <button
            onClick={handleShare}
            disabled={shareLoading}
            style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.5rem', padding: '0.5rem 1.1rem', cursor: shareLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: shareLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {shareLoading ? '⏳' : '🔗'} Partager
          </button>
          {shareUrl && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem 1rem', zIndex: 50, minWidth: '280px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '0.4rem', fontWeight: '600' }}>Lien de paiement :</div>
              <div style={{ color: '#60a5fa', fontSize: '0.78rem', wordBreak: 'break-all', fontFamily: 'monospace' }}>{shareUrl}</div>
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast('Copié !', 'success'); }} style={{ marginTop: '0.6rem', background: 'rgba(59,130,246,0.15)', border: 'none', color: '#60a5fa', borderRadius: '0.35rem', padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>
                📋 Copier le lien
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Changement de statut */}
      {transitions.length > 0 && (
        <div style={{ background: 'rgba(30,41,59,0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600' }}>Changer le statut :</span>
          {transitions.map(s => {
            const cfg = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                disabled={statusLoading}
                onClick={() => handleStatus(s)}
                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: '0.5rem', padding: '0.45rem 1rem', cursor: statusLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.85rem', opacity: statusLoading ? 0.6 : 1, transition: 'all 0.15s' }}
              >
                {STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Émetteur */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Émetteur</h3>
          {artisanProfile ? (
            <>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '0.5rem' }}>{artisanProfile.nom_entreprise || artisanProfile.name}</p>
              {artisanProfile.metier && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.metier}</p>}
              {artisanProfile.adresse && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.adresse}</p>}
              {(artisanProfile.code_postal || artisanProfile.ville) && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{[artisanProfile.code_postal, artisanProfile.ville].filter(Boolean).join(' ')}</p>}
              {artisanProfile.email_pro && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.email_pro}</p>}
              {artisanProfile.siret && <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.25rem' }}>SIRET : {artisanProfile.siret}</p>}
              {artisanProfile.tva_number && <p style={{ color: '#64748b', fontSize: '0.8rem' }}>TVA : {artisanProfile.tva_number}</p>}
            </>
          ) : (
            <p style={{ color: '#475569', fontSize: '0.85rem' }}>Profil non renseigné</p>
          )}
        </div>

        {/* Client */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Client</h3>
          {invoice.client_name ? (
            <>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '0.5rem' }}>{invoice.client_name}</p>
              {invoice.client_email && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{invoice.client_email}</p>}
              {invoice.client_phone && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{invoice.client_phone}</p>}
              {invoice.client_address && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{invoice.client_address}</p>}
            </>
          ) : (
            <p style={{ color: '#475569', fontSize: '0.85rem' }}>Aucun client associé</p>
          )}
        </div>
      </div>

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Détails facture */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Détails</h3>
          <InfoRow label="Numéro" value={invoice.number} />
          <InfoRow label="Date d'émission" value={fmt(invoice.date)} />
          <InfoRow label="Date d'échéance" value={fmt(invoice.due_date)} />
          <InfoRow label="Statut" value={sc.label} />
        </div>

        {/* Montants */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Montants</h3>
          <InfoRow label="Montant HT" value={fmtAmount(ht)} />
          <InfoRow label={tvaLabel} value={fmtAmount(tva)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', marginTop: '0.25rem', borderTop: '2px solid #3b82f6' }}>
            <span style={{ color: 'white', fontWeight: '700', fontSize: '0.95rem' }}>Total TTC</span>
            <span style={{ color: '#60a5fa', fontWeight: '800', fontSize: '1.25rem' }}>{fmtAmount(ttc)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.75rem' }}>Notes / Prestations</h3>
          <p style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0 }}>{invoice.notes}</p>
        </div>
      )}

      {/* IBAN */}
      {artisanProfile?.iban && (
        <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '0.75rem', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '1.2rem' }}>🏦</span>
          <div>
            <span style={{ color: '#34d399', fontWeight: '700', fontSize: '0.85rem' }}>Coordonnées bancaires</span>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0.2rem 0 0' }}>
              IBAN : {artisanProfile.iban}{artisanProfile.bic ? ` — BIC : ${artisanProfile.bic}` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
